<template>
  <div class="container">
    <el-card class="box-card" :body-style="{ padding: '0' }">
      <!-- 表格头部操作栏 -->
      <div style="display: flex; flex-wrap: wrap; align-items: center; gap: 16px; padding: 0; width: 100%;">
        <!-- 日期选择器 -->
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          @change="handleDateRangeChange"
          style="width: 260px; flex-shrink: 0;">
        </el-date-picker>

        <!-- 角色筛选 -->
        <el-select
          v-if="isSuperAdmin"
          v-model="params.query.role"
          clearable
          placeholder="请选择角色"
          @clear="roleClear"
          @change="roleSearch"
          style="width: 140px; flex-shrink: 0;">
          <el-option
            v-for="role in filteredRoleListForSearch"
            :key="role.roleId"
            :label="role.roleName"
            :value="role.roleId">
            <span>{{ role.roleName }}</span>
          </el-option>
        </el-select>

        <!-- 用户筛选 -->
        <el-select
          v-model="params.query.userId"
          clearable
          placeholder="请选择用户"
          @clear="userClear"
          @change="userSearch"
          style="width: 160px; flex-shrink: 0;">
          <el-option
            v-for="user in userList"
            :key="user.userId"
            :label="user.userAccount"
            :value="user.userId">
            <span>{{ user.userAccount }}</span>
          </el-option>
        </el-select>

        <!-- 关键字搜索 -->
        <el-input
          placeholder="请输入关键字搜索"
          v-model="params.query.selectdata"
          clearable
          @clear="contentClear"
          style="width: 260px; flex-shrink: 0;">
          <el-button slot="append" icon="el-icon-search" @click="contentSearch"></el-button>
        </el-input>

        <!-- 重置按钮 -->
        <el-button type="primary" @click="clear" style="flex-shrink: 0; margin: 0;">重置</el-button>

        <!-- 新增用户按钮 -->
        <el-button v-if="hasManagePermission" type="primary" @click="handleAddUserClick" style="flex-shrink: 0; margin: 0;">新增用户</el-button>

        <!-- 占位空间：自动占满剩余空间 -->
        <div style="flex: 1;"></div>

        <!-- 刷新按钮：永远在最右边 -->
        <div style="align-self: flex-start; flex-shrink: 0;">
          <el-button type="text" icon="el-icon-refresh" @click="refreshData" style="color: gray; padding: 0;">刷新</el-button>
        </div>
      </div>

      <!-- 用户列表区域 - 超级管理员显示树形表格 -->
      <div class="table-container">
        <el-table
          v-if="isSuperAdmin"
          ref="treeTable"
          :key="treeTableKey"
          :data="treeTableData"
          border
          stripe
          height="100%"
          fit
          :row-style="{height: '6vh'}"
          :cell-style="{padding: '0'}"
          row-key="userId"
          lazy
          :load="loadChildNodes"
          :tree-props="{children: 'children', hasChildren: 'hasChildren'}">

          <el-table-column label="创建时间" min-width="152" align="center" show-overflow-tooltip>
            <template #default="{ row }">
              {{ formatDateTime(row.createdTime) }}
            </template>
          </el-table-column>

          <el-table-column label="账号" min-width="280">
            <template #default="{ row }">
              <div :style="{ marginLeft: (row.level || 0) * 20 + 'px' }">
                <span v-if="row.hasChildren" style="color: #3498db; font-size: 15px; margin-right: 8px;">●</span>
                <span v-if="row.level > 0" style="padding-left: 3%;">|-</span>
                {{ row.userAccount }}
                <el-button
                  v-if="canShowMoveButton(row)"
                  type="text"
                  icon="el-icon-rank"
                  size="small"
                  style="margin-left: 10px; color: #409EFF;"
                  @click.stop="showMoveUserDialog(row)">移动</el-button>
                <el-button
                  v-if="canShowUnbindButton(row)"
                  type="text"
                  icon="el-icon-unlock"
                  size="small"
                  style="margin-left: 10px; color: #F56C6C;"
                  @click.stop="showUnbindUserDialog(row)">解绑</el-button>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="角色" min-width="100" align="center" show-overflow-tooltip>
            <template #default="{ row }">
              <span>{{ getRoleName(row.role) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="邮箱" prop="userEmail" min-width="130" align="center" show-overflow-tooltip></el-table-column>

          <el-table-column label="绑定批次号" min-width="220" align="center">
            <template #default="{ row }">
              <div class="batch-cell" v-if="row.batchNos && row.batchNos.length > 0">
                <div class="batch-list">
                  <el-tooltip
                    effect="dark"
                    :content="row.batchNos[0]"
                    placement="top"
                    :disabled="row.batchNos[0].length <= 16">
                    <el-tag type="primary" size="small" style="cursor: pointer; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                      {{ row.batchNos[0] }}
                    </el-tag>
                  </el-tooltip>
                  <el-popover
                    v-if="row.batchNos.length > 1"
                    placement="bottom"
                    width="300"
                    trigger="hover">
                    <div class="batch-popover-content">
                      <div class="batch-popover-title">共绑定 {{ row.batchNos.length }} 个批次号</div>
                      <div class="batch-popover-list">
                        <el-tooltip
                          v-for="(batchNo, index) in row.batchNos"
                          :key="index"
                          effect="dark"
                          :content="batchNo"
                          placement="top"
                          :disabled="batchNo.length <= 16">
                          <el-tag
                            type="primary"
                            size="small"
                            style="margin: 2px; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: pointer;">
                            {{ batchNo }}
                          </el-tag>
                        </el-tooltip>
                      </div>
                    </div>
                    <el-tag type="warning" size="small" style="margin-left: 5px; cursor: pointer;" slot="reference">
                      +{{ row.batchNos.length - 1 }}
                    </el-tag>
                  </el-popover>
                </div>
              </div>
              <span v-else style="color: #999">未绑定</span>
            </template>
          </el-table-column>

          <el-table-column label="启用状态" min-width="80" align="center">
            <template #default="{ row }">
              <el-switch
                v-model="row.active"
                :active-value="true"
                :inactive-value="false"
                :disabled="isSwitchDisabled(row)"
                @change="handleSwitchChange(row)">
              </el-switch>
            </template>
          </el-table-column>

          <el-table-column label="操作" min-width="200" align="center">
            <template #default="{ row }">
              <div class="button-wrapper">
                <div class="button-item">
                  <el-tooltip
                    v-if="isRoleButtonDisabled(row)"
                    effect="dark"
                    :content="getRoleButtonDisabledReason(row)"
                    placement="top">
                    <el-button
                      type="primary"
                      size="mini"
                      disabled>角色</el-button>
                  </el-tooltip>
                  <el-button
                    v-else
                    type="primary"
                    size="mini"
                    @click.stop="setRole(row.userId)">角色</el-button>
                </div>
                <div class="button-item">
                  <el-button
                    type="warning"
                    size="mini"
                    :disabled="row.userAccount === 'admin' && !isAdminAccount()"
                    @click.stop="showBindBatchNoDrawer(row.userId, row.batchNos)">绑定</el-button>
                </div>
                <div class="button-item">
                  <el-button
                    type="success"
                    size="mini"
                    :disabled="row.userAccount === 'admin' && !isAdminAccount()"
                    @click.stop="resetPassword(row.userId)">密码</el-button>
                </div>
                <div class="button-item">
                  <el-tooltip
                    v-if="row.userAccount === 'admin'"
                    effect="dark"
                    content="admin账号不可删除"
                    placement="top">
                    <el-button type="danger" size="mini" disabled>删除</el-button>
                  </el-tooltip>
                  <el-button
                    v-else
                    type="danger"
                    size="mini"
                    @click.stop="removeUserById(row.userId)">删除</el-button>
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 普通管理员：普通表格 -->
        <el-table v-else :data="userDateList"
                  border
                  stripe
                  height="100%"
                  :row-style="{height: '6vh'}"
                  :cell-style="{padding: '0'}">
          <el-table-column label="创建时间"  min-width="152" align="center" show-overflow-tooltip>
            <template #default="{ row }">
              {{ formatDateTime(row.createdTime) }}
            </template>
          </el-table-column>
          <el-table-column label="账号" prop="userAccount" min-width="200" align="center" show-overflow-tooltip></el-table-column>

          <el-table-column label="角色"  min-width="100" align="center" show-overflow-tooltip>
            <template #default="{ row }">
              <span>{{ getRoleName(row.role) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="邮箱" prop="userEmail" min-width="130" align="center" show-overflow-tooltip></el-table-column>

          <el-table-column label="绑定批次号" min-width="280" align="center">
            <template #default="{ row }">
              <div class="batch-cell" v-if="row.batchNos && row.batchNos.length > 0">
                <div class="batch-list">
                  <el-tooltip
                    effect="dark"
                    :content="row.batchNos[0]"
                    placement="top"
                    :disabled="row.batchNos[0].length <= 16">
                    <el-tag type="primary" size="small" style="cursor: pointer; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                      {{ row.batchNos[0] }}
                    </el-tag>
                  </el-tooltip>
                  <el-popover
                    v-if="row.batchNos.length > 1"
                    placement="bottom"
                    width="300"
                    trigger="hover">
                    <div class="batch-popover-content">
                      <div class="batch-popover-title">共绑定 {{ row.batchNos.length }} 个批次号</div>
                      <div class="batch-popover-list">
                        <el-tooltip
                          v-for="(batchNo, index) in row.batchNos"
                          :key="index"
                          effect="dark"
                          :content="batchNo"
                          placement="top"
                          :disabled="batchNo.length <= 16">
                          <el-tag
                            type="primary"
                            size="small"
                            style="margin: 2px; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: pointer;">
                            {{ batchNo }}
                          </el-tag>
                        </el-tooltip>
                      </div>
                    </div>
                    <el-tag type="warning" size="small" style="margin-left: 5px; cursor: pointer;" slot="reference">
                      +{{ row.batchNos.length - 1 }}
                    </el-tag>
                  </el-popover>
                </div>
              </div>
              <span v-else style="color: #999">未绑定</span>
            </template>
          </el-table-column>

          <el-table-column label="启用状态" min-width="80" align="center">
            <template #default="{ row }">
              <el-switch
                v-model="row.active"
                :active-value="true"
                :inactive-value="false"
                :disabled="isSwitchDisabled(row)"
                @change="handleSwitchChange(row)">
              </el-switch>
            </template>
          </el-table-column>

          <el-table-column label="操作" min-width="150" align="center">
            <template #default="{ row }">
              <div v-if="canOperateNormalUser(row)" class="button-wrapper">
                <div class="button-item">
                  <el-button type="warning" size="mini" @click.stop="showBindBatchNoDrawer(row.userId, row.batchNos)">绑定</el-button>
                </div>
                <div class="button-item">
                  <el-button type="success" size="mini" @click.stop="resetPassword(row.userId)">密码</el-button>
                </div>
                <div class="button-item">
                  <!-- 当前账号禁用删除按钮 -->
                  <el-button
                    type="danger"
                    size="mini"
                    :disabled="row.userAccount === userAccount"
                    @click.stop="removeUserById(row.userId)">删除</el-button>
                </div>
              </div>
              <div v-else style="color: #999; text-align: center;">无权操作</div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页区域 -->
      <div>
        <el-pagination
          ref="pagination"
          v-if="!isSuperAdmin"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="params.current"
          :page-sizes="[20, 50, 100, 1000]"
          :page-size="params.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
        <el-pagination
          v-else
          ref="pagination"
          @size-change="handleTreePageSizeChange"
          @current-change="handleTreePageCurrentChange"
          :current-page="treePageParams.current"
          :page-sizes="[20, 50, 100, 1000]"
          :page-size="treePageParams.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="treeTotal">
        </el-pagination>
      </div>
    </el-card>

    <!-- 新增用户的对话框 -->
    <el-dialog title="新增用户" :visible.sync="addDialogVisible"
               :modal="false"
               :close-on-click-modal="false"
               width="820px"
               @close="addDialogClosed">
      <div class="add-user-layout">
        <!-- 左侧：表单 -->
        <div class="add-user-left">
          <el-form ref="addFormRef" :model="addUserForm" label-width="90px" :rules="FormRules">
            <el-form-item label="账号" prop="userAccount">
              <el-input v-model="addUserForm.userAccount" clearable placeholder="请输入用户账号"></el-input>
            </el-form-item>
            <el-form-item label="密码" prop="password">
              <el-input v-model="addUserForm.password" clearable placeholder="请输入用户账号密码" show-password></el-input>
            </el-form-item>
            <el-form-item label="邮箱" prop="userEmail">
              <el-input v-model="addUserForm.userEmail" clearable placeholder="请输入邮箱"></el-input>
            </el-form-item>
            <!-- 只有超级管理员才显示上级管理员选择 -->
            <el-form-item label="上级管理员" prop="parentUserId" v-if="isSuperAdmin">
              <el-select
                v-model="addUserForm.parentUserId"
                placeholder="请选择上级管理员"
                clearable
                filterable
                style="width: 100%;"
                @change="onParentUserIdChange">
                <el-option
                  v-for="user in adminUserOptions"
                  :key="user.userId"
                  :label="user.userAccount"
                  :value="user.userId">
                  <span style="float: left">{{ user.userAccount }}</span>
                  <span style="float: right; color: #8492a6; font-size: 13px">{{ user.roleName }}</span>
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="角色" v-if="isSuperAdmin">
              <el-select v-model="addUserForm.role" placeholder="请选择角色" clearable style="width: 100%;">
                <el-option
                  v-for="role in availableRolesForAdd"
                  :key="role.roleId"
                  :label="role.roleName"
                  :value="role.roleId">
                </el-option>
              </el-select>
            </el-form-item>
          </el-form>
        </div>

        <!-- 分割线 -->
        <div class="add-user-divider"></div>

        <!-- 右侧：绑定批次号 -->
        <div class="add-user-right">
          <div class="batch-title">
            <span>绑定批次号</span>
            <span class="batch-count" v-if="addUserForm.batchNos.length > 0">({{ addUserForm.batchNos.length }})</span>
          </div>

          <div class="batch-select-wrapper">
            <!-- 空状态 -->
            <div v-if="addUserForm.batchNos.length === 0" class="batch-empty">
              <i class="el-icon-document"></i>
              <span>暂未选择批次号</span>
            </div>

            <!-- 批次号列表 - 块级渲染，每行一条 -->
            <div v-else class="batch-list-scroll">
              <div
                v-for="(batch, index) in addUserForm.batchNos"
                :key="index"
                class="batch-item">
                <el-tooltip
                  effect="dark"
                  :content="batch"
                  placement="top"
                  :disabled="batch.length <= 16">
                  <span class="batch-item-text">{{ batch }}</span>
                </el-tooltip>
                <span class="batch-item-close" @click="removeBatchFromAddForm(index)">×</span>
              </div>
            </div>

            <el-button type="primary" size="small" plain @click="openBatchSelectorForAdd" class="batch-select-btn">
              <i class="el-icon-plus"></i> 选择批次号
            </el-button>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="addDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="addUser" :loading="addUserLoading">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 移动用户的对话框 -->
    <el-dialog title="移动用户" :visible.sync="moveDialogVisible"
               :modal="false"
               :close-on-click-modal="false"
               width="450px"
               @close="moveDialogClosed">
      <el-form label-width="80px">
        <el-form-item label="当前用户:">
          <span>{{ moveUser.userAccount }}</span>
        </el-form-item>
        <el-form-item label="当前上级:">
          <span>{{ moveUser.parentUserAccount || '无' }}</span>
        </el-form-item>
        <el-form-item label="移动到:">
          <el-select v-model="moveTargetParentId" placeholder="请选择目标上级管理员" clearable filterable style="width: 100%;">
            <el-option
              v-for="parent in availableParents"
              :key="parent.userId"
              :label="parent.userAccount"
              :value="parent.userId">
              <span style="float: left">{{ parent.userAccount }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ parent.roleName }}</span>
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" style="display: flex; justify-content: center; gap: 20px;">
        <el-button @click="moveDialogVisible = false">取 消</el-button>
        <!-- 添加 :disabled 属性，没有选择目标时禁用确认按钮 -->
        <el-button
          type="primary"
          @click="confirmMoveUser"
          :loading="moveUserLoading"
          :disabled="!moveTargetParentId">
          确 定
        </el-button>
      </div>
    </el-dialog>

    <!-- 批次号选择抽屉 -->
    <el-drawer
      :visible.sync="batchDrawerVisible"
      direction="rtl"
      size="60%"
      :modal="false"
      :close-on-click-modal="false"
      @close="batchDrawerClosed">
      <!-- 自定义标题 -->
      <template #title>
        <span style="color: #000000; font-weight: 500; font-size: 18px;">选择批次号</span>
      </template>
      <div class="batch-select-container">
        <div class="batch-search-bar">
          <el-input
            placeholder="请输入批次号"
            v-model="batchSearchKeyword"
            clearable
            @clear="searchBatchList"
            @keyup.enter.native="searchBatchList"
            style="width: 300px; margin-right: 10px">
            <el-button slot="append" icon="el-icon-search" @click="searchBatchList" :loading="batchLoading"></el-button>
          </el-input>
          <el-button type="primary" @click="confirmBatchSelection">确 定 选 择</el-button>
          <span class="selected-count">已选择: {{ tempSelectedBatchNos.length }} 个批次号</span>
        </div>
        <div class="table-container">
          <el-table
            ref="batchTable"
            :data="filteredBatchList"
            border
            stripe
            height="100%"
            :row-style="{height: '5.82vh'}"
            :cell-style="{padding: '0'}"
            v-loading="batchLoading">

            <!-- 自定义复选框列（原生 checkbox） -->
            <el-table-column
              label=""
              width="55"
              align="center">
              <template #header>
                <input type="checkbox" v-model="batchSelectAll" @change="toggleBatchSelectAll"/>
              </template>
              <template #default="{ row }">
                <input type="checkbox" v-model="tempSelectedBatchNos" :value="row.batchNo"/>
              </template>
            </el-table-column>

            <el-table-column label="批次号" prop="batchNo" width="200" align="center" show-overflow-tooltip></el-table-column>
            <!-- 批次号备注列 -->
            <el-table-column label="批次号备注" :min-width="280" align="center">
              <template slot-scope="{ row }">
                <div class="remark-cell">
                  <!-- 超级管理员：可以查看和编辑所有备注 -->
                  <template v-if="isSuperAdmin">
                    <el-tooltip
                      effect="dark"
                      :content="row.remark || '-'"
                      placement="top"
                      :disabled="!row.remark || row.remark.length <= 20">
          <span class="remark-text remark-ellipsis">
            {{ row.remark || '-' }}
          </span>
                    </el-tooltip>
                    <el-button
                      type="text"
                      icon="el-icon-edit"
                      size="small"
                      @click="showEditRemarkDialog(row)"
                      style="flex-shrink: 0; padding: 0 4px;">
                    </el-button>
                  </template>

                  <!-- 普通管理员：只能查看和编辑自己创建的批次号备注 -->
                  <template v-else-if="row.createUserId === userId">
                    <el-tooltip
                      effect="dark"
                      :content="row.remark || '-'"
                      placement="top"
                      :disabled="!row.remark || row.remark.length <= 20">
          <span class="remark-text remark-ellipsis">
            {{ row.remark || '-' }}
          </span>
                    </el-tooltip>
                    <el-button
                      type="text"
                      icon="el-icon-edit"
                      size="small"
                      @click="showEditRemarkDialog(row)"
                      style="flex-shrink: 0; padding: 0 4px;">
                    </el-button>
                  </template>

                  <!-- 非自己创建的批次号，显示空 -->
                  <template v-else>
                    <span style="color: #ccc; font-size: 12px;">-</span>
                  </template>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="创建时间" width="200" align="center" show-overflow-tooltip>
              <template #default="{ row }">
                {{ formatDateTime(row.createdTime) }}
              </template>
            </el-table-column>
          </el-table>
        </div>

          <el-pagination
            ref="pagination"
            @size-change="handleBatchSizeChange"
            @current-change="handleBatchCurrentChange"
            :current-page="batchParams.current"
            :page-sizes="[20, 50, 100]"
            :page-size="batchParams.size"
            layout="total, sizes, prev, pager, next, jumper"
            :total="batchTotal">
          </el-pagination>
      </div>
    </el-drawer>

    <!-- 分配角色的对话框 -->
    <el-dialog
      title="分配角色"
      :modal="false"
      :close-on-click-modal="false"
      :visible.sync="setRoleDialogVisible"
      width="450px"
      @close="setRoleDialogClosed">
      <el-form label-width="80px">
        <el-form-item label="当前用户:">
          <span>{{ currentUserAccount }}</span>
        </el-form-item>
        <el-form-item label="当前角色:">
          <span>{{ currentUserRoleName }}</span>
        </el-form-item>
        <el-form-item label="分配角色:">
          <el-select v-model="selectedRoleId" placeholder="请选择" clearable style="width: 200px;">
            <el-option
              v-for="role in availableRolesForAssign"
              :key="role.roleId"
              :label="role.roleName"
              :value="role.roleId">
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="setRoleDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveRoleInfo" :loading="saveRoleLoading">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 重置密码对话框 -->
    <el-dialog :visible.sync="resetDialogVisible"
               :modal="false"
               :close-on-click-modal="false"
               title="重置密码"
               width="450px"
               @close="resetDialogClosed">
      <el-form ref="resetFormRef" :model="resetForm" label-width="80px" :rules="resetFormRules">
        <el-form-item label="新密码" prop="password">
          <el-input v-model="resetForm.password"
                    type="password"
                    clearable
                    show-password
                    placeholder="请输入重置密码"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="resetDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="resetCode" :loading="resetPasswordLoading">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 编辑批次号备注对话框 -->
    <el-dialog title="编辑批次号备注" :visible.sync="editRemarkDialogVisible"
               :modal="false"
               :close-on-click-modal="false"
               width="550px"
               @close="editRemarkDialogClosed">
      <el-form ref="editRemarkFormRef" :model="editRemarkForm" label-width="100px">
        <el-form-item label="设备批次号">
          <el-input v-model="editRemarkForm.batchNo" disabled></el-input>
        </el-form-item>
        <el-form-item label="批次号备注">
          <el-input
            v-model="editRemarkForm.remark"
            type="textarea"
            :rows="4"
            clearable
            placeholder="请输入批次号备注信息"
            maxlength="200"
            show-word-limit>
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editRemarkDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="updateRemark">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import paginationMixin from '@/components/mixins/pagination'
import JSEncrypt from 'jsencrypt'
import { Base64 } from 'js-base64'
import { mapActions, mapGetters } from 'vuex'

// 角色常量
const ROLE = {
  SUPER_ADMIN: '0', // 超级管理员
  ADMIN: '1', // 管理员
  NORMAL: '2' // 普通用户
}

export default {
  mixins: [paginationMixin],
  data() {
    // 自定义账号验证函数（必填，用于 userAccount）
    const validateAccount = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入账号'))
        return
      }
      if (value.length < 2 || value.length > 18) {
        callback(new Error('账号长度必须为2-18位'))
        return
      }
      if (/^\d+$/.test(value)) {
        callback(new Error('账号不能全是数字'))
        return
      }
      // 允许中文、英文、数字、下划线、@、.
      if (!/^[\u4e00-\u9fa5a-zA-Z0-9_@.]+$/.test(value)) {
        callback(new Error('账号只能包含中文、英文、数字、下划线、@、.'))
        return
      }
      callback()
    }
    return {
      // ==================== 基础数据 ====================
      publicKey: null,
      currentUserRoleName: '',
      currentUserAccount: '',
      treeTableKey: 0, // 用于强制刷新树形表格
      batchSelectAll: false, // 批次号抽屉全选复选框状态
      dateRange: [], // 日期范围
      isFromDrawer: false,

      // ==================== 表单校验规则 ====================
      FormRules: {
        userAccount: [
          { required: true, message: '请输入账号', trigger: 'blur' },
          { validator: validateAccount, trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 15, message: '密码的长度在6~15个字符之间', trigger: 'blur' },
          {
            pattern: /^[A-Za-z0-9!@#$%^&*()_+={}[\]:;"'<>,.?/~`-]+$/,
            message: '密码只能包含英文字母、数字和特殊字符，不允许中文',
            trigger: 'blur'
          }
        ],
        userEmail: [
          {
            pattern: /^[a-zA-Z0-9_\-.]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,4}$/,
            message: '邮箱号格式不正确：正确格式为 xxx@xxx.xx',
            trigger: 'blur'
          }
        ]
      },
      // 重置密码表单校验
      resetFormRules: {
        password: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, max: 15, message: '密码长度必须在 6 ~ 15 位', trigger: 'blur' }
        ]
      },

      // ==================== 分页参数 ====================
      params: {
        query: {
          selectdata: '',
          userId: '',
          role: '',
          startDate: '',
          endDate: ''
        },
        current: 1,
        size: 20
      },
      treePageParams: {
        current: 1,
        size: 20,
        query: {
          selectdata: '',
          userId: '',
          role: '',
          startDate: '',
          endDate: ''
        }
      },
      batchParams: {
        current: 1,
        size: 20
      },

      // ==================== 列表数据 ====================
      userDateList: [], // 普通用户列表
      userList: [], // 用户下拉列表
      roleList: [], // 角色列表
      adminUserOptions: [], // 管理员选项
      batchNoList: [], // 批次号列表
      allBatchList: [], // 全部批次号列表（用于下拉框）
      filteredBatchList: [], // 筛选后的批次号列表（用于表格）
      treeTableData: [], // 树形表格数据

      // ==================== 总数统计 ====================
      total: 0, // 普通用户总数
      treeTotal: 0, // 树形用户总数
      batchTotal: 0, // 批次号总数

      // ==================== 对话框可见性 ====================
      addDialogVisible: false, // 新增用户对话框
      resetDialogVisible: false, // 重置密码对话框
      batchDrawerVisible: false, // 批次号选择抽屉
      moveDialogVisible: false, // 移动用户对话框
      setRoleDialogVisible: false, // 分配角色对话框
      editRemarkDialogVisible: false, // 编辑批次号备注对话框

      // ==================== 表单数据 ====================
      addUserForm: {
        userAccount: '',
        password: '',
        userEmail: '',
        parentUserId: '',
        role: ROLE.NORMAL, // 默认普通用户
        batchNos: []
      },
      resetForm: {
        password: ''
      },
      editRemarkForm: {
        batchId: '',
        batchNo: '',
        remark: ''
      },

      // ==================== 加载状态 ====================
      addUserLoading: false, // 新增用户加载状态
      resetPasswordLoading: false, // 重置密码加载状态
      moveUserLoading: false, // 移动用户加载状态
      saveRoleLoading: false, // 保存角色加载状态
      batchLoading: false, // 批次号列表加载状态

      // ==================== 临时数据 ====================
      tempSelectedBatchNos: [], // 临时选中的批次号
      currentBindUserId: null, // 当前绑定的用户ID
      currentBindContext: null, // 当前绑定上下文（add/edit/bind）
      autoBindAllBatches: false, // 自动绑定所有批次号
      moveUser: { // 移动用户信息
        userId: '',
        userAccount: '',
        parentUserAccount: ''
      },
      moveTargetParentId: '', // 移动目标上级ID
      availableParents: [], // 可选的上级列表
      roleButtonCache: new Map(), // 角色按钮缓存
      batchSearchKeyword: '', // 批次号搜索关键词

      // ==================== 角色分配相关 ====================
      userInfo: {},
      selectedRoleId: '',
      targetUserId: null
    }
  },

  computed: {
    ...mapGetters('globalUserInfo', [
      'userAccount',
      'userId',
      'roleId',
      'isSuperAdmin',
      'isNormalAdmin',
      'hasManagePermission',
      'isAdmin'
    ]),

    /**
     * 筛选后的角色列表（用于搜索下拉框）
     * 超级管理员：超级管理员可以看到所有角色
     * 普通管理员：只显示普通用户
     */
    filteredRoleListForSearch() {
      if (this.isSuperAdmin) {
        // 超级管理员可以看到所有角色（包括超级管理员自己）
        return this.roleList
      }
      if (this.isNormalAdmin) {
        return this.roleList.filter(role => {
          const roleId = String(role.roleId)
          return roleId === ROLE.NORMAL
        })
      }
      return []
    },

    /**
     * 可用的角色列表（新增用户时）
     * 有上级时：只能创建普通用户
     * 无上级时：可以创建管理员和普通用户
     */
    availableRolesForAdd() {
      if (!this.hasManagePermission) return []
      // 如果有上级（子账号），只能创建普通用户
      if (this.addUserForm.parentUserId) {
        return this.roleList.filter(role => {
          const roleId = String(role.roleId)
          return roleId === ROLE.NORMAL
        })
      }
      // 没有上级时，可以创建管理员和普通用户
      return this.roleList.filter(role => {
        const roleId = String(role.roleId)
        return roleId === ROLE.ADMIN || roleId === ROLE.NORMAL
      })
    },

    /**
     * 可分配的角色列表
     * 排除超级管理员角色
     */
    availableRolesForAssign() {
      if (!this.hasManagePermission) return []
      return this.roleList.filter(role => {
        const roleId = String(role.roleId)
        return roleId !== ROLE.SUPER_ADMIN
      })
    }
  },

  async created() {
    const { params } = await this.$store.dispatch('tabs/restorePageCache', {
      key: 'userManagementCache',
      defaultParams: this.params
    })
    const [cachedUserId, cachedRole] = [params.query.userId, params.query.role]
    params.query.userId = params.query.role = ''
    this.params = params
    this.treePageParams = { ...params, query: { ...params.query } }
    this.dateRange = params.query.startDate && params.query.endDate ? [params.query.startDate, params.query.endDate] : []
    // 只加载辅助数据（角色、用户下拉、管理员选项、批次号）
    await this.initData()
    if (cachedUserId) {
      this.params.query.userId = this.treePageParams.query.userId = cachedUserId
    }
    if (cachedRole) {
      this.params.query.role = this.treePageParams.query.role = cachedRole
    }
    // 只加载一次列表
    this.isSuperAdmin ? this.getUserTreePage() : this.getUserPage()
  },

  methods: {
    // ==================== 初始化方法 ====================
    ...mapActions('tabs', ['savePageCache']),

    /**
     * 初始化数据
     * 加载角色列表、用户列表、管理员选项、批次号列表
     */
    async initData() {
      try {
        // 只加载辅助数据，不加载列表
        await Promise.all([
          this.getRoleList(),
          this.getUserList(),
          this.getAdminUserOptions(),
          this.getAllBatchList()
        ])
      } catch (error) {
        console.error('初始化辅助数据失败', error)
      }
    },

    /**
     * 刷新数据（重置所有筛选条件）
     */
    async refreshData() {
      this.roleButtonCache.clear()
      if (this.isSuperAdmin) {
        this.treePageParams.current = 1
        this.treePageParams.query = { selectdata: '', userId: '', role: '', startDate: '', endDate: '' }
        this.dateRange = []
        await this.initData()
        await this.getUserTreePage()
      } else {
        this.params.current = 1
        this.params.query = { selectdata: '', userId: '', role: '', startDate: '', endDate: '' }
        this.dateRange = []
        await this.initData()
        await this.getUserPage()
      }
    },

    // ==================== 日期筛选相关方法 ====================

    /**
     * 日期范围变化处理
     * @param {Array} value - 日期范围 [startDate, endDate]
     */
    handleDateRangeChange(value) {
      if (value && value.length === 2) {
        this.params.query.startDate = value[0]
        this.params.query.endDate = value[1]
      } else {
        this.params.query.startDate = ''
        this.params.query.endDate = ''
      }
      if (this.isSuperAdmin) {
        this.treePageParams.query.startDate = this.params.query.startDate
        this.treePageParams.query.endDate = this.params.query.endDate
        this.treePageParams.current = 1
        this.getUserTreePage()
      } else {
        this.params.current = 1
        this.getUserPage()
      }
    },

    // ==================== 用户列表相关方法 ====================

    /**
     * 获取用户分页列表（普通管理员表格）
     */
    async getUserPage() {
      const { data: result } = await this.$http.post('/user/page', this.params)
      if (result.status !== 200) return this.$message.error(result.message)
      let records = result.data.userPage.records || []
      this.userDateList = records
      this.total = result.data.userPage.total || 0
      if (this.isNormalAdmin) {
        this.total = this.userDateList.length
      }
      this.savePageCache({
        key: 'userManagementCache',
        params: this.params
      })
    },

    /**
     * 获取用户列表（用于下拉框）
     */
    async getUserList() {
      if (this.isNormalAdmin) {
        if (this.userDateList && this.userDateList.length > 0) {
          // 包含所有用户（包括管理员自己和所有普通用户）
          this.userList = this.userDateList.map(user => ({
            userId: user.userId,
            userAccount: user.userAccount
          }))
        } else {
          await this.getUserPage()
          this.userList = this.userDateList.map(user => ({
            userId: user.userId,
            userAccount: user.userAccount
          }))
        }
      } else {
        try {
          const { data: result } = await this.$http.get('/user/list')
          if (result.status !== 200) {
            console.error('获取用户列表失败:', result.message)
            return
          }
          this.userList = (result.data.records || []).map(user => ({
            userId: user.userId,
            userAccount: user.userAccount
          }))
        } catch (error) {
          console.error('获取用户列表异常:', error)
          this.userList = []
        }
      }
    },

    /**
     * 获取树形用户分页列表（超级管理员专用）
     */
    async getUserTreePage() {
      try {
        this.treeTableData = []
        const { data: result } = await this.$http.post('/user/treePage', {
          current: this.treePageParams.current,
          size: this.treePageParams.size,
          query: this.treePageParams.query
        })
        if (result.status === 200) {
          const records = result.data.records || []
          const filteredRecords = this.isAdminAccount() ? records : records.filter(user => user.userAccount !== 'admin')
          this.treeTableData = filteredRecords.map(user => ({
            userId: user.userId,
            userAccount: user.userAccount,
            role: user.role,
            userEmail: user.userEmail,
            createdTime: user.createdTime,
            active: user.active,
            hasChildren: user.hasChildren === true,
            parentUserId: user.parentUserId,
            parentUserAccount: user.parentUserAccount,
            batchNos: user.batchNos || [],
            level: user.level || 0
          }))
          this.treeTotal = result.data.total || 0
          this.roleButtonCache.clear()
          this.savePageCache({
            key: 'userManagementCache',
            params: this.treePageParams
          })
        }
      } catch (error) {
        console.error('获取树形分页数据失败', error)
        this.treeTableData = []
        this.$message.error('获取数据失败')
      }
    },

    /**
     * 加载子节点（树形表格懒加载）
     * @param {Object} row - 当前行数据
     * @param {Object} treeNode - 树节点
     * @param {Function} resolve - 回调函数
     */
    async loadChildNodes(row, treeNode, resolve) {
      try {
        const { data: result } = await this.$http.get(`/user/children/${row.userId}`)
        if (result.status === 200) {
          const children = result.data || []
          const formattedChildren = children.map(child => ({
            userId: child.userId,
            userAccount: child.userAccount,
            role: child.role,
            userEmail: child.userEmail,
            createdTime: child.createdTime,
            active: child.active,
            hasChildren: child.hasChildren === true,
            parentUserId: child.parentUserId,
            parentUserAccount: child.parentUserAccount,
            batchNos: child.batchNos || [],
            level: (row.level || 0) + 1
          }))
          resolve(formattedChildren)
        } else {
          resolve([])
        }
      } catch (error) {
        console.error('加载子节点失败', error)
        resolve([])
      }
    },

    /**
     * 强制刷新树形表格
     */
    async refreshTreeTable() {
      this.treeTableKey = Date.now()
      this.treeTableData = []
      await this.getUserTreePage()
    },

    /**
     * 获取管理员选项（用于上级选择）
     * 管理员角色ID = 1
     */
    async getAdminUserOptions() {
      try {
        const { data: result } = await this.$http.get('/user/list')
        if (result.status === 200) {
          let allUsers = result.data.records || []
          if (!this.isAdminAccount()) {
            allUsers = allUsers.filter(user => user.userAccount !== 'admin')
          }
          this.adminUserOptions = allUsers
            .filter(user => String(user.role) === ROLE.ADMIN)
            .map(user => ({
              userId: user.userId,
              userAccount: user.userAccount,
              roleName: this.getRoleName(user.role)
            }))
        } else {
          this.adminUserOptions = []
        }
      } catch (error) {
        console.error('获取管理员选项失败', error)
        this.adminUserOptions = []
      }
    },

    // ==================== 角色相关方法 ====================

    /**
     * 获取角色列表
     */
    async getRoleList() {
      const { data: result } = await this.$http.get('/role/list')
      if (!result.success) return this.$message.error(result.message)
      this.roleList = result.data
    },

    /**
     * 获取角色名称
     * @param {string|number} roleId - 角色ID
     * @returns {string} 角色名称
     */
    getRoleName(roleId) {
      if (!roleId) return '-'
      const role = this.roleList.find(r => String(r.roleId) === String(roleId))
      return role ? role.roleName : roleId
    },

    // ==================== 批次号相关方法 ====================
    /**
     * 获取批次号列表（统一的分页/不分页方法）
     * @param {Object} options - 配置选项
     * @param {boolean} options.isSearch - 是否为搜索操作（搜索时不更新 allBatchList）
     * @param {boolean} options.isPage - 是否分页（true:分页 false:不分页获取全部）
     */
    async getAllBatchList({ isSearch = false, isPage = true } = {}) {
      try {
        this.batchLoading = true
        const query = {}

        if (this.batchSearchKeyword && this.batchSearchKeyword.trim()) {
          query.batchNo = this.batchSearchKeyword.trim()
        }

        const params = isPage ? {
          current: this.batchParams.current,
          size: this.batchParams.size,
          query
        } : {
          current: 1,
          size: 9999,
          query
        }

        const { data: result } = await this.$http.post('/deviceBatch/page', params)

        if (result.status === 200) {
          const records = result.data.deviceBatchPage.records || []
          const total = result.data.deviceBatchPage.total || 0

          this.filteredBatchList = records
          this.batchTotal = total

          if (!isPage && !isSearch) {
            this.allBatchList = records
          }
        } else {
          this.filteredBatchList = []
          this.batchTotal = 0
          if (!isPage && !isSearch) {
            this.allBatchList = []
          }
        }
      } catch (error) {
        console.error('获取批次号列表失败', error)
        this.$message.error('获取批次号列表失败，请刷新页面重试')
        this.filteredBatchList = []
        this.batchTotal = 0
        if (!isPage && !isSearch) {
          this.allBatchList = []
        }
      } finally {
        this.batchLoading = false
      }
    },

    /**
     * 搜索批次号（分页）
     */
    searchBatchList() {
      this.batchParams.current = 1
      this.getAllBatchList({ isSearch: true, isPage: true })
      this.$nextTick(() => {
        this.restoreSelectionsInCurrentPage()
      })
    },

    /**
     * 批次号分页-每页条数变化
     * @param {number} size - 每页条数
     */
    handleBatchSizeChange(size) {
      this.batchParams.size = size
      this.batchParams.current = 1
      this.getAllBatchList({ isSearch: true, isPage: true })
      this.$nextTick(() => {
        this.restoreSelectionsInCurrentPage()
      })
    },

    /**
     * 批次号分页-页码变化
     * @param {number} page - 当前页码
     */
    handleBatchCurrentChange(page) {
      this.batchParams.current = page
      this.getAllBatchList({ isSearch: true, isPage: true })
      this.$nextTick(() => {
        this.restoreSelectionsInCurrentPage()
      })
    },

    /**
     * 批次号抽屉全选/取消全选
     */
    toggleBatchSelectAll() {
      if (this.batchSelectAll) {
        // 全选：添加当前页所有批次号到选中列表（去重）
        const currentPageBatchNos = this.filteredBatchList.map(item => item.batchNo)
        const newSelected = [...new Set([...this.tempSelectedBatchNos, ...currentPageBatchNos])]
        this.tempSelectedBatchNos = newSelected
      } else {
        // 取消全选：移除当前页所有批次号
        const currentPageBatchNos = this.filteredBatchList.map(item => item.batchNo)
        this.tempSelectedBatchNos = this.tempSelectedBatchNos.filter(
          batchNo => !currentPageBatchNos.includes(batchNo)
        )
      }

      // 更新表格的选中状态
      this.$nextTick(() => {
        this.updateTableSelection()
      })
    },

    /**
     * 更新表格选中状态
     */
    updateTableSelection() {
      if (!this.$refs.batchTable) return
      if (!this.filteredBatchList || this.filteredBatchList.length === 0) return

      this.$refs.batchTable.clearSelection()
      const selectedSet = new Set(this.tempSelectedBatchNos)
      const selectedRowsInCurrentPage = this.filteredBatchList.filter(item =>
        selectedSet.has(item.batchNo)
      )
      selectedRowsInCurrentPage.forEach(row => {
        this.$refs.batchTable.toggleRowSelection(row, true)
      })
    },

    /**
     * 恢复当前页的选中状态
     */
    restoreSelectionsInCurrentPage() {
      if (!this.$refs.batchTable) return
      if (!this.filteredBatchList || this.filteredBatchList.length === 0) return

      this.$refs.batchTable.clearSelection()
      const selectedSet = new Set(this.tempSelectedBatchNos)
      const selectedRowsInCurrentPage = this.filteredBatchList.filter(item =>
        selectedSet.has(item.batchNo)
      )
      selectedRowsInCurrentPage.forEach(row => {
        this.$refs.batchTable.toggleRowSelection(row, true)
      })

      // 更新全选状态
      const currentPageBatchNos = this.filteredBatchList.map(item => item.batchNo)
      const selectedInCurrentPage = this.tempSelectedBatchNos.filter(batchNo =>
        currentPageBatchNos.includes(batchNo)
      )
      this.batchSelectAll = selectedInCurrentPage.length === this.filteredBatchList.length && this.filteredBatchList.length > 0
    },

    /**
     * 打开批次号选择器（新增用户）
     */
    async openBatchSelectorForAdd() {
      this.currentBindContext = 'add'
      this.isFromDrawer = true // 标记是从抽屉返回
      this.tempSelectedBatchNos = [...this.addUserForm.batchNos]
      this.batchSearchKeyword = ''
      this.batchParams.current = 1
      this.batchParams.size = 20

      this.addDialogVisible = false
      this.batchDrawerVisible = true
      await this.getAllBatchList({ isSearch: true, isPage: true })
      await this.$nextTick()
      setTimeout(() => {
        this.restoreSelectionsInCurrentPage()
      }, 200)
    },

    /**
     * 打开批次号选择器（绑定用户）
     * @param {string} userId - 用户ID
     * @param {Array} existingBatchNos - 已绑定的批次号列表
     */
    async showBindBatchNoDrawer(userId, existingBatchNos) {
      if (!this.hasManagePermission) {
        this.$message.warning('您没有相关权限，请联系管理员进行操作')
        return
      }
      this.currentBindContext = 'bind'
      this.currentBindUserId = userId
      this.tempSelectedBatchNos = [...(existingBatchNos || [])]
      this.batchSearchKeyword = ''
      this.batchParams.current = 1
      this.batchParams.size = 20
      this.batchDrawerVisible = true
      await this.getAllBatchList({ isSearch: true, isPage: true })
      await this.$nextTick()
      setTimeout(() => {
        this.restoreSelectionsInCurrentPage()
      }, 200)
    },

    /**
     * 批次号抽屉关闭回调
     */
    batchDrawerClosed() {
      if (this.currentBindContext === 'add') {
        // 标记是从抽屉返回，防止清空表单
        this.isFromDrawer = true
        this.addDialogVisible = true
      }
      this.$nextTick(() => {
        this.tempSelectedBatchNos = []
        this.currentBindUserId = null
        this.currentBindContext = null
        this.batchSearchKeyword = ''
        this.batchParams.current = 1
        if (this.$refs.batchTable) {
          this.$refs.batchTable.clearSelection()
        }
      })
    },

    /**
     * 确认批次号选择
     */
    confirmBatchSelection() {
      if (this.currentBindContext === 'add') {
        this.addUserForm.batchNos = [...this.tempSelectedBatchNos]
        this.batchDrawerVisible = false
        // 标记是从抽屉返回，防止清空表单
        this.isFromDrawer = true
        this.$nextTick(() => {
          this.addDialogVisible = true
        })
      } else if (this.currentBindContext === 'bind') {
        this.saveBatchBind()
        return
      }
    },

    /**
     * 保存批次号绑定
     */
    async saveBatchBind() {
      try {
        const { data: result } = await this.$http.post('/userBatchRelation/bindBatch', {
          userId: this.currentBindUserId,
          batchNos: this.tempSelectedBatchNos
        })
        if (result.status === 200) {
          this.batchDrawerVisible = false

          const updateUserBatch = (list) => {
            const user = list.find(u => u.userId === this.currentBindUserId)
            if (user) {
              user.batchNos = [...this.tempSelectedBatchNos]
              return true
            }
            return false
          }

          if (this.isSuperAdmin) {
            if (!updateUserBatch(this.treeTableData)) {
              await this.getUserTreePage()
            }
          } else {
            if (!updateUserBatch(this.userDateList)) {
              await this.getUserPage()
            }
          }
        } else {
          this.$message.error(result.message || '绑定失败')
        }
      } catch (error) {
        this.$message.error('绑定失败，请稍后重试')
      }
    },

    /**
     * 从新增表单中移除批次号
     * @param {number} index - 批次号索引
     */
    removeBatchFromAddForm(index) {
      this.addUserForm.batchNos.splice(index, 1)
    },

    // ==================== 编辑批次号备注相关方法 ====================
    /**
     * 显示编辑备注对话框
     * @param {Object} row - 批次号行数据
     */
    showEditRemarkDialog(row) {
      // 超级管理员：可以编辑所有备注
      if (this.isSuperAdmin) {
        this.editRemarkForm = {
          batchId: row.batchId,
          batchNo: row.batchNo,
          remark: row.remark || ''
        }
        this.editRemarkDialogVisible = true
        return
      }

      // 只能编辑自己创建的
      if (row.createUserId !== this.userId) {
        this.$message.warning('您只能编辑自己创建的批次号备注')
        return
      }

      this.editRemarkForm = {
        batchId: row.batchId,
        batchNo: row.batchNo,
        remark: row.remark || ''
      }
      this.editRemarkDialogVisible = true
    },

    /**
     * 更新批次号备注
     * 编辑成功后只更新当前行的备注数据，不刷新整个列表，保持选中状态
     */
    async updateRemark() {
      try {
        const { data: result } = await this.$http.post('/deviceBatch/updateRemark', {
          batchId: this.editRemarkForm.batchId,
          remark: this.editRemarkForm.remark
        })

        if (result.status === 200) {
          this.$message.success('备注更新成功')
          this.editRemarkDialogVisible = false

          const updatedBatchNo = this.editRemarkForm.batchNo
          const updatedRemark = this.editRemarkForm.remark

          // 1. 更新当前表格数据（filteredBatchList）- 只更新备注字段
          const targetInFiltered = this.filteredBatchList.find(item => item.batchNo === updatedBatchNo)
          if (targetInFiltered) {
            targetInFiltered.remark = updatedRemark
          }

          // 2. 更新全部批次号数据（allBatchList，用于下拉框）- 只更新备注字段
          const targetInAll = this.allBatchList.find(item => item.batchNo === updatedBatchNo)
          if (targetInAll) {
            targetInAll.remark = updatedRemark
          }

          // 3. 如果有表格引用，刷新表格显示（不改变选中状态）
          if (this.$refs.batchTable) {
            this.$forceUpdate()
          }
        } else {
          this.$message.error(result.message || '更新失败')
        }
      } catch (error) {
        console.error('更新备注失败:', error)
        this.$message.error('更新备注失败，请稍后重试')
      }
    },

    /**
     * 关闭编辑备注对话框回调
     */
    editRemarkDialogClosed() {
      if (this.$refs.editRemarkFormRef) {
        this.$refs.editRemarkFormRef.resetFields()
      }
    },

    // ==================== 用户操作相关方法 ====================

    /**
     * 新增用户按钮点击
     */
    async handleAddUserClick() {
      if (!this.hasManagePermission) {
        this.$message.warning('您没有相关权限，请联系管理员进行操作')
        return
      }
      if (this.allBatchList.length === 0) {
        await this.getAllBatchList({ isSearch: false, isPage: false })
      }
      await this.getAdminUserOptions()
      this.addUserForm = {
        userAccount: '',
        password: '',
        userEmail: '',
        parentUserId: '',
        role: ROLE.NORMAL, // 默认普通用户
        batchNos: []
      }
      this.addDialogVisible = true
    },

    /**
     * 新增用户
     */
    async addUser() {
      // 防止重复点击
      if (this.addUserLoading) {
        this.$message.warning('正在提交中，请勿重复操作')
        return
      }

      this.$refs.addFormRef.validate(async valid => {
        if (!valid) {
          return
        }

        this.addUserLoading = true
        try {
          await this.fetchPublicKey()
          const encryptedPassword = this.encryptString(this.addUserForm.password)
          const base64EncodedPassword = Base64.encode(encryptedPassword)
          let role = this.addUserForm.role
          if (!role || role === null || role === '') {
            role = ROLE.NORMAL // 默认普通用户
          }

          // 普通管理员新增用户时，自动将当前登录用户设置为上级
          let parentUserId = this.addUserForm.parentUserId || null
          if (this.isNormalAdmin && !parentUserId) {
            parentUserId = this.userId // 当前登录管理员的ID
          }

          // 保存用户账号用于成功提示
          const userAccount = this.addUserForm.userAccount

          const formData = {
            userAccount: this.addUserForm.userAccount,
            password: base64EncodedPassword,
            userEmail: this.addUserForm.userEmail,
            parentUserId: parentUserId,
            role,
            batchNos: this.addUserForm.batchNos
          }

          const { data: result } = await this.$http.post('/user/add', formData)
          if (result.status !== 200) {
            this.$message.error(result.message)
            return
          }

          this.$message.success(`新增用户 "${userAccount}" 成功！`)
          this.addDialogVisible = false
          this.getUserPage()
          this.getUserList()
          if (this.isSuperAdmin) {
            this.getUserTreePage()
            this.getAdminUserOptions()
          }
        } catch (error) {
          console.error('新增用户失败', error)
          this.$message.error('新增用户失败，请稍后重试')
        } finally {
          this.addUserLoading = false
        }
      })
    },

    /**
     * 新增用户对话框关闭回调
     */
    addDialogClosed() {
      if (this.isFromDrawer) {
        this.isFromDrawer = false
        return
      }
      if (this.$refs.addFormRef) {
        this.$refs.addFormRef.resetFields()
      }
      this.addUserForm.batchNos = []
      this.addUserForm.role = ROLE.NORMAL
      this.addUserForm.parentUserId = ''
      this.addUserLoading = false
    },

    /**
     * 上级管理员变化回调
     */
    onParentUserIdChange() {
      // 有上级时，角色只能选择普通用户
      if (this.addUserForm.parentUserId) {
        this.addUserForm.role = ROLE.NORMAL
      }
    },

    async removeUserById(userId) {
      // 只有超级管理员或普通管理员可以操作
      if (!this.isSuperAdmin && !this.isNormalAdmin) {
        this.$message.warning('您没有相关权限，请联系管理员进行操作')
        return
      }
      let targetUser = null
      let actionType = 'delete' // 默认删除
      try {
        // 获取用户信息
        const { data: userResult } = await this.$http.post(`/user/getUser/${userId}`)
        targetUser = userResult
        // 不能删除admin账号
        if (targetUser.userAccount === 'admin') {
          this.$message.warning('不能删除admin账号！')
          return
        }
        // ========== 判断操作类型 ==========
        if (!this.isSuperAdmin && this.isNormalAdmin) {
          // 普通管理员：判断 createUserId 是否等于自己
          if (targetUser.createUserId === this.userId) {
            actionType = 'delete' // 自己创建 → 真删除
          } else {
            actionType = 'unbind' // 非自己创建 → 解绑
          }
        }
        // 超级管理员始终是 delete
      } catch (e) {
        this.$message.error('获取用户信息失败，无法操作')
        return
      }
      const userAccount = targetUser.userAccount
      // 统一确认提示
      const confirmResult = await this.$confirm(
        `此操作将永久删除用户 "${userAccount}", 是否继续?`,
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).catch(err => err)
      if (confirmResult !== 'confirm') {
        return
      }
      try {
        let result
        if (actionType === 'delete') {
          result = await this.$http.delete(`user/delete/${userId}`)
        } else {
          result = await this.$http.post('/user/unbindFromParent', { userId })
        }
        // 检查业务状态码（Restful 的 status 字段）
        if (result.data && result.data.status === 200) {
          // 统一成功提示
          this.$message.success(result.data.message || `用户 "${userAccount}" 删除成功！`)
          // 刷新列表
          if (this.isSuperAdmin) {
            await this.refreshTreeTable()
          } else {
            await this.getUserPage()
          }
          await this.getUserList()
          if (this.isSuperAdmin) {
            await this.getAdminUserOptions()
          }
          // 操作后检查当前页是否有数据
          if (this.isSuperAdmin) {
            if (this.treeTableData.length === 0 && this.treePageParams.current > 1) {
              this.treePageParams.current -= 1
              await this.getUserTreePage()
            }
          } else {
            if (this.userDateList.length === 0 && this.params.current > 1) {
              this.params.current -= 1
              await this.getUserPage()
            }
          }
        } else {
          // 统一错误提示
          this.$message.warning((result.data && result.data.message) || `用户 "${userAccount}" 删除失败`)
        }
      } catch (error) {
        console.error('操作失败', error)
        this.$message.error(`用户 "${userAccount}" 删除失败，请稍后重试`)
      }
    },

    /**
     * 重置密码按钮点击
     * @param {string} userId - 用户ID
     */
    async resetPassword(userId) {
      if (!this.hasManagePermission) {
        this.$message.warning('您没有相关权限，请联系管理员进行操作')
        return
      }
      this.resetForm.userId = userId
      this.resetForm.password = ''
      this.resetDialogVisible = true
    },

    /**
     * 重置密码对话框关闭回调
     */
    resetDialogClosed() {
      this.$refs.resetFormRef && this.$refs.resetFormRef.resetFields()
    },

    /**
     * 重置密码
     */
    resetCode() {
      this.$refs.resetFormRef.validate(async valid => {
        if (!valid) return
        if (this.resetPasswordLoading) {
          this.$message.warning('正在提交中，请勿重复操作')
          return
        }

        this.resetPasswordLoading = true
        try {
          await this.fetchPublicKey()
          const encryptedPassword = this.encryptString(this.resetForm.password)
          const base64EncodedPassword = Base64.encode(encryptedPassword)

          const { data: result } = await this.$http.post('/user/resetPassword', {
            userId: this.resetForm.userId,
            password: base64EncodedPassword
          })
          if (result.status !== 200) {
            this.$message.error('重置密码失败！')
            return
          }
          this.resetDialogVisible = false
          this.$message.success('重置密码成功！')
        } catch (err) {
          this.$message.error('重置密码失败，请稍后重试')
        } finally {
          this.resetPasswordLoading = false
        }
      })
    },

    /**
     * 获取RSA公钥
     */
    async fetchPublicKey() {
      try {
        const { data } = await this.$http.get('/rsa/getPublicKey')
        this.publicKey = data
      } catch (error) {
        this.$message.error('操作失败，请刷新重试')
      }
    },

    /**
     * RSA加密字符串
     * @param {string} str - 待加密字符串
     * @returns {string} 加密后的字符串
     */
    encryptString(str) {
      const encrypt = new JSEncrypt()
      encrypt.setPublicKey(this.publicKey)
      return encrypt.encrypt(str)
    },

    // ==================== 角色分配相关方法 ====================

    /**
     * 设置用户角色按钮点击
     * @param {string} userId - 用户ID
     */
    async setRole(userId) {
      const result = await this.$http.post(`/user/getUser/${userId}`)
      const userData = result.data
      const roleId = String(userData.role)

      // 管理员（role=1）不能有子账号，如果有子账号则不能修改角色
      if ((roleId === ROLE.ADMIN || roleId === ROLE.SUPER_ADMIN) && userData.hasChildren === true) {
        this.$message.warning(`用户 "${userData.userAccount}" 下存在子账号，请先解绑所有子账号后再修改角色！`)
        return
      }
      // 普通用户有上级时，不能修改角色
      if ((roleId === ROLE.NORMAL) && userData.parentUserId) {
        this.$message.warning(`用户 "${userData.userAccount}" 当前是其他用户的子账号，不能直接分配管理员角色！请先将该用户从其上级解绑后再操作`)
        return
      }
      if (userData.userAccount === 'admin') {
        this.$message.warning('admin账号不能被分配角色')
        return
      }

      await this.fetchUserData(userId)
      this.targetUserId = userId
      this.setRoleDialogVisible = true
    },

    /**
     * 获取用户数据（用于角色分配）
     * @param {string} userId - 用户ID
     */
    async fetchUserData(userId) {
      try {
        const result = await this.$http.post(`/user/getUser/${userId}`)
        const userData = result.data
        this.currentUserAccount = userData.userAccount
        this.currentUserRoleName = this.getRoleName(userData.role)
      } catch (error) {
        this.$message.error('获取用户信息失败，请稍后重试！')
      }
    },

    /**
     * 保存角色分配
     */
    async saveRoleInfo() {
      if (!this.selectedRoleId) {
        return this.$message.warning('请选择要分配的角色！')
      }

      const selectedRole = this.roleList.find(r => String(r.roleId) === String(this.selectedRoleId))
      if (selectedRole && selectedRole.roleId === ROLE.SUPER_ADMIN) {
        return this.$message.warning('不能分配超级管理员角色！')
      }
      if (this.isNormalAdmin) {
        if (selectedRole && (selectedRole.roleId === ROLE.SUPER_ADMIN || selectedRole.roleId === ROLE.ADMIN)) {
          return this.$message.warning('您没有权限分配管理员角色！')
        }
      }

      if (this.saveRoleLoading) {
        this.$message.warning('正在提交中，请勿重复操作')
        return
      }

      this.saveRoleLoading = true
      try {
        const formData = new FormData()
        formData.append('userId', this.targetUserId)
        formData.append('role', this.selectedRoleId)

        const { data: result } = await this.$http.post('user/updateRole', formData)

        if (result.status === 200) {
          const updateUserRole = (list) => {
            const user = list.find(u => u.userId === this.targetUserId)
            if (user) {
              user.role = this.selectedRoleId
              return true
            }
            return false
          }

          if (this.isSuperAdmin) {
            if (!updateUserRole(this.treeTableData)) {
              await this.getUserTreePage()
            }
          } else {
            if (!updateUserRole(this.userDateList)) {
              await this.getUserPage()
            }
          }

          this.getUserList()
          if (this.isSuperAdmin) {
            this.getAdminUserOptions()
          }

          this.setRoleDialogVisible = false
          this.selectedRoleId = ''
          this.roleButtonCache.clear()
        } else {
          this.$message.error(result.message)
        }
      } catch (error) {
        console.error('更新角色失败', error)
        this.$message.error('更新角色请求失败，请稍后重试！')
      } finally {
        this.saveRoleLoading = false
      }
    },

    /**
     * 角色分配对话框关闭回调
     */
    setRoleDialogClosed() {
      this.selectedRoleId = ''
      this.userInfo = {}
      this.targetUserId = null
    },

    // ==================== 用户状态切换相关方法 ====================

    /**
     * 用户启用状态切换
     * @param {Object} row - 用户行数据
     */
    handleSwitchChange(row) {
      if (!this.isSwitchDisabled(row)) {
        this.toggleActiveStatus(row)
      }
    },

    /**
     * 切换用户启用状态
     * @param {Object} row - 用户行数据
     */
    async toggleActiveStatus(row) {
      try {
        const result = await this.$http.post('/user/updateActive', {
          userId: row.userId,
          active: row.active
        })
        if (result.status === 200) {
          this.$message.success(`${row.userAccount}账号${row.active ? '已启用' : '已停用'}`)
          this.getUserList()
        }
      } catch (error) {
        row.active = !row.active
        this.$message.warning('状态更新失败，请稍后再试')
      }
    },

    // ==================== 用户移动相关方法 ====================

    /**
     * 显示移动用户对话框（只有超级管理员可以移动普通用户）
     * @param {Object} row - 用户行数据
     */
    async showMoveUserDialog(row) {
      // 只有超级管理员可以移动
      if (!this.isSuperAdmin) {
        this.$message.warning('只有超级管理员可以移动用户')
        return
      }

      // 只能移动普通用户
      if (String(row.role) !== ROLE.NORMAL) {
        this.$message.warning('只能移动普通用户')
        return
      }

      // 获取最新的用户信息
      let currentParentUserId = null
      try {
        const { data: userResult } = await this.$http.post(`/user/getUser/${row.userId}`)
        const latestUser = userResult
        currentParentUserId = latestUser.parentUserId
        this.moveUser = {
          userId: row.userId,
          userAccount: row.userAccount,
          parentUserAccount: latestUser.parentUserAccount || '无',
          parentUserId: latestUser.parentUserId
        }
      } catch (error) {
        this.moveUser = {
          userId: row.userId,
          userAccount: row.userAccount,
          parentUserAccount: row.parentUserAccount || '无',
          parentUserId: row.parentUserId
        }
        currentParentUserId = row.parentUserId
      }
      this.moveTargetParentId = ''
      this.moveDialogVisible = true
      this.availableParents = []
      // 获取可选的上级列表（管理员）
      try {
        const { data: result } = await this.$http.get(`/user/availableParents/${row.userId}`)
        if (result.status === 200) {
          let parents = (result.data || []).map(admin => ({
            userId: admin.userId,
            userAccount: admin.userAccount,
            roleName: this.getRoleName(admin.role),
            role: admin.role
          }))
          // 只显示管理员作为可选的上级
          parents = parents.filter(parent => String(parent.role) === ROLE.ADMIN)
          // 排除当前已有的上级
          if (currentParentUserId) {
            parents = parents.filter(parent => parent.userId !== currentParentUserId)
          }
          // 排除自己
          parents = parents.filter(parent => parent.userId !== this.moveUser.userId)
          this.availableParents = parents
        } else {
          this.availableParents = []
          this.$message.warning(result.message || '获取可选上级列表失败')
        }
      } catch (error) {
        console.error('获取可选上级列表失败', error)
        this.$message.error('获取可选上级列表失败')
        this.availableParents = []
      }
    },

    /**
     * 确认移动用户
     */
    async confirmMoveUser() {
      // 允许清空上级（移动到顶层）
      // 如果 moveTargetParentId 为空字符串或 null，表示移动到顶层
      if (this.moveTargetParentId === this.moveUser.parentUserId) {
        this.$message.warning('不能移动到当前已绑定的上级')
        return
      }
      if (this.moveTargetParentId === this.moveUser.userId) {
        this.$message.warning('不能移动到自己名下')
        return
      }
      if (this.moveUserLoading) {
        this.$message.warning('正在提交中，请勿重复操作')
        return
      }
      this.moveUserLoading = true
      try {
        // 如果 moveTargetParentId 为空，传 null 表示移动到顶层（无上级）
        const targetParentId = this.moveTargetParentId || null
        const { data: result } = await this.$http.post('/user/moveToParent', {
          userId: this.moveUser.userId,
          targetParentUserId: targetParentId
        })
        if (result.status === 200) {
          this.moveDialogVisible = false

          // 先刷新列表获取最新数据
          await this.refreshTreeTable()
          await this.getUserList()

          // 检查当前页是否有数据，如果没有且不是第一页，则跳转到上一页
          if (this.treeTableData.length === 0 && this.treePageParams.current > 1) {
            this.treePageParams.current -= 1
            await this.getUserTreePage() // 重新加载上一页数据
          }

          setTimeout(() => {
            this.getAdminUserOptions()
          }, 300)
        } else {
          this.$message.error(result.message || '移动用户失败')
        }
      } catch (error) {
        console.error('移动用户失败详情:', error)
        this.$message.error('移动用户失败，请稍后重试')
      } finally {
        this.moveUserLoading = false
      }
    },

    /**
     * 移动用户对话框关闭回调
     */
    moveDialogClosed() {
      this.moveUser = {
        userId: '',
        userAccount: '',
        parentUserAccount: ''
      }
      this.moveTargetParentId = ''
      this.availableParents = []
      this.moveUserLoading = false
    },

    // ==================== 用户解绑相关方法 ====================

    /**
     * 判断是否显示解绑按钮
     * @param {Object} row - 用户行数据
     * @returns {boolean}
     */
    canShowUnbindButton(row) {
      if (!this.isSuperAdmin) return false
      if (row.userAccount === 'admin') return false
      const roleId = String(row.role)
      return roleId === ROLE.NORMAL && row.parentUserId
    },

    /**
     * 显示解绑用户对话框
     * @param {Object} row - 用户行数据
     */
    async showUnbindUserDialog(row) {
      // 普通管理员解绑自己的子账号
      if (this.isNormalAdmin) {
        // 检查这个用户是否是当前管理员的子账号
        const isSubAccount = row.parentUserId === this.userId
        if (!isSubAccount) {
          this.$message.warning('只能解绑自己名下的子账号')
          return
        }
      }
      // 超级管理员解绑
      if (this.isSuperAdmin) {
        const roleId = String(row.role)
        if (roleId === ROLE.ADMIN || roleId === ROLE.SUPER_ADMIN) {
          this.$message.warning('管理员不能被解绑')
          return
        }
        if (row.userAccount === 'admin') {
          this.$message.warning('admin账号不可解绑')
          return
        }
        if (!row.parentUserId) {
          this.$message.warning('该用户没有上级，无需解绑')
          return
        }
      }
      const confirmResult = await this.$confirm(
        `确定要将用户 "${row.userAccount}" 从其上级解绑吗？解绑后该用户将变为独立的用户。`,
        '解绑确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).catch(err => err)

      if (confirmResult !== 'confirm') {
        return
      }
      await this.confirmUnbindUser(row)
    },

    /**
     * 确认解绑用户
     * @param {Object} row - 用户行数据
     */
    async confirmUnbindUser(row) {
      const loading = this.$loading({
        lock: true,
        text: '正在解绑中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      try {
        const { data: result } = await this.$http.post('/user/unbindFromParent', {
          userId: row.userId
        })

        if (result.status === 200) {
          if (this.isSuperAdmin) {
            await this.refreshTreeTable()
          } else {
            await this.getUserPage()
          }
          await this.getUserList()
          if (this.isSuperAdmin) {
            await this.getAdminUserOptions()
          }
        } else {
          this.$message.error(result.message || '解绑失败')
        }
      } catch (error) {
        console.error('解绑失败', error)
        this.$message.error('解绑失败，请稍后重试')
      } finally {
        loading.close()
      }
    },

    // ==================== 按钮权限判断方法 ====================

    /**
     * 判断是否显示移动按钮
     * @param {Object} row - 用户行数据
     * @returns {boolean}
     */
    canShowMoveButton(row) {
      if (!this.isSuperAdmin) return false
      if (row.userAccount === 'admin') return false
      const roleId = String(row.role)
      return roleId === ROLE.NORMAL
    },

    /**
     * 判断角色按钮是否禁用
     * @param {Object} row - 用户行数据
     * @returns {boolean}
     */
    isRoleButtonDisabled(row) {
      if (row.userAccount === 'admin') return true
      const roleId = String(row.role)
      if ((roleId === ROLE.ADMIN || roleId === ROLE.SUPER_ADMIN) && row.hasChildren === true) return true
      if (roleId === ROLE.NORMAL && row.parentUserId) return true
      return false
    },

    /**
     * 获取角色按钮禁用原因
     * @param {Object} row - 用户行数据
     * @returns {string}
     */
    getRoleButtonDisabledReason(row) {
      if (row.userAccount === 'admin') return 'admin账号不可分配角色'
      const roleId = String(row.role)
      if ((roleId === ROLE.ADMIN || roleId === ROLE.SUPER_ADMIN) && row.hasChildren === true) {
        return '该用户下存在子账号，请先解绑所有子账号再操作'
      }
      if (roleId === ROLE.NORMAL && row.parentUserId) {
        return '该用户当前是子账号，请先解绑后再操作！'
      }
      return ''  // 可用时返回空
    },

    /**
     * 判断开关是否禁用
     * @param {Object} row - 用户行数据
     * @returns {boolean}
     */
    isSwitchDisabled(row) {
      if (row.userAccount === 'admin') return true
      if (this.isSuperAdmin) return false
      if (this.isNormalAdmin) {
        // 当前账号的启用开关禁用
        if (row.userAccount === this.userAccount) return true
        const role = String(row.role)
        return role !== ROLE.NORMAL
      }
      return true
    },

    /**
     * 判断是否可以操作普通用户
     * @param {Object} row - 用户行数据
     * @returns {boolean}
     */
    canOperateNormalUser(row) {
      if (row.userAccount === 'admin') return false
      // 当前账号可以操作（绑定、密码可见）
      if (row.userAccount === this.userAccount) return true  // 改为 true
      const role = String(row.role)
      return role === ROLE.NORMAL
    },

    /**
     * 判断是否为admin账户
     * @returns {boolean}
     */
    isAdminAccount() {
      return this.userAccount === 'admin'
    },

    // ==================== 分页相关方法 ====================

    /**
     * 处理树形表格每页条数变化
     * @param {number} size - 每页条数
     */
    handleTreePageSizeChange(size) {
      this.treePageParams.size = size
      this.treePageParams.current = 1
      this.getUserTreePage()
    },

    /**
     * 处理树形表格页码变化
     * @param {number} page - 当前页码
     */
    handleTreePageCurrentChange(page) {
      this.treePageParams.current = page
      this.getUserTreePage()
    },

    /**
     * 处理普通表格每页条数变化
     * @param {number} newSize - 每页条数
     */
    handleSizeChange(newSize) {
      this.params.size = newSize
      this.getUserPage()
    },

    /**
     * 处理普通表格页码变化
     * @param {number} newPage - 当前页码
     */
    handleCurrentChange(newPage) {
      this.params.current = newPage
      this.getUserPage()
    },

    // ==================== 搜索筛选方法 ====================

    /**
     * 用户筛选
     */
    userSearch() {
      if (this.isSuperAdmin) {
        this.treePageParams.current = 1
        this.treePageParams.query.userId = this.params.query.userId
        this.getUserTreePage()
      } else {
        this.params.current = 1
        this.getUserPage()
      }
    },

    /**
     * 角色筛选
     */
    roleSearch() {
      if (this.isSuperAdmin) {
        this.treePageParams.current = 1
        this.treePageParams.query.role = this.params.query.role
        this.getUserTreePage()
      } else {
        this.params.current = 1
        this.getUserPage()
      }
    },

    /**
     * 内容搜索
     */
    contentSearch() {
      if (this.isSuperAdmin) {
        this.treePageParams.current = 1
        this.treePageParams.query.selectdata = this.params.query.selectdata
        this.getUserTreePage()
      } else {
        this.params.current = 1
        this.getUserPage()
      }
    },

    /**
     * 清空用户筛选
     */
    userClear() {
      if (this.isSuperAdmin) {
        this.treePageParams.query.userId = ''
        this.treePageParams.current = 1
        this.getUserTreePage()
      } else {
        this.userResetFlag()
        this.getUserPage()
      }
    },

    /**
     * 清空角色筛选
     */
    roleClear() {
      if (this.isSuperAdmin) {
        this.treePageParams.query.role = ''
        this.treePageParams.current = 1
        this.getUserTreePage()
      } else {
        this.roleResetFlag()
        this.getUserPage()
      }
    },

    /**
     * 清空内容筛选
     */
    contentClear() {
      if (this.isSuperAdmin) {
        this.treePageParams.query.selectdata = ''
        this.treePageParams.current = 1
        this.getUserTreePage()
      } else {
        this.contentResetFlag()
        this.getUserPage()
      }
    },

    /**
     * 重置所有筛选条件
     */
    clear() {
      if (this.isSuperAdmin) {
        this.treePageParams.query = { selectdata: '', userId: '', role: '', startDate: '', endDate: '' }
        this.treePageParams.current = 1
        this.params.query = { selectdata: '', userId: '', role: '', startDate: '', endDate: '' }
        this.dateRange = []
        this.getUserTreePage()
      } else {
        this.params.query = { selectdata: '', userId: '', role: '', startDate: '', endDate: '' }
        this.dateRange = []
        this.userResetFlag()
        this.roleResetFlag()
        this.contentResetFlag()
        this.getUserPage()
      }
    },

    /**
     * 重置用户筛选标志
     */
    userResetFlag() {
      this.params.query.userId = ''
      this.params.current = 1
    },

    /**
     * 重置角色筛选标志
     */
    roleResetFlag() {
      this.params.query.role = ''
      this.params.current = 1
    },

    /**
     * 重置内容筛选标志
     */
    contentResetFlag() {
      this.params.query.selectdata = ''
      this.params.current = 1
    },

    // ==================== 工具方法 ====================

    /**
     * 格式化日期时间
     * @param {string} dateTime - 日期时间字符串
     * @returns {string} 格式化后的日期时间
     */
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      if (typeof dateTime === 'string' && dateTime.includes('T')) {
        return dateTime.replace('T', ' ').substring(0, 19)
      }
      return dateTime
    },

    /**
     * 检查角色按钮禁用状态（带缓存）
     * @param {Object} row - 用户行数据
     * @returns {Object} 禁用状态和原因
     */
    async checkRoleButtonDisabled(row) {
      const cacheKey = `${row.userId}_${row.role}_${row.hasChildren}_${row.parentUserId}`
      if (this.roleButtonCache.has(cacheKey)) {
        return this.roleButtonCache.get(cacheKey)
      }

      let result = { disabled: false, reason: '' }
      if (row.userAccount === 'admin') {
        result = { disabled: true, reason: 'admin账号不可分配角色' }
        this.roleButtonCache.set(cacheKey, result)
        return result
      }

      const roleId = String(row.role)
      if ((roleId === ROLE.ADMIN || roleId === ROLE.SUPER_ADMIN) && row.hasChildren === true) {
        result = { disabled: true, reason: '该用户下存在子账号，请先解绑所有子账号后再修改角色' }
        this.roleButtonCache.set(cacheKey, result)
        return result
      }

      if (roleId === ROLE.NORMAL && row.parentUserId) {
        result = { disabled: true, reason: '该用户当前是其他用户的子账号，不能直接分配管理员角色！请先将该用户从其上级解绑后再操作' }
        this.roleButtonCache.set(cacheKey, result)
        return result
      }

      this.roleButtonCache.set(cacheKey, { disabled: false, reason: '分配角色' })
      return { disabled: false, reason: '分配角色' }
    },

    /**
     * 判断用户是否有子节点
     * @param {Object} row - 用户行数据
     * @returns {boolean}
     */
    hasChildren(row) {
      return row.hasChildren === true
    }
  },
  watch: {
    // 监听批次号选中列表变化，更新全选状态
    tempSelectedBatchNos: {
      handler(newVal) {
        if (this.filteredBatchList.length === 0) {
          this.batchSelectAll = false
          return
        }
        // 检查当前页是否全部选中
        const currentPageBatchNos = this.filteredBatchList.map(item => item.batchNo)
        const selectedInCurrentPage = newVal.filter(batchNo =>
          currentPageBatchNos.includes(batchNo)
        )
        this.batchSelectAll = selectedInCurrentPage.length === this.filteredBatchList.length && this.filteredBatchList.length > 0
      },
      deep: true
    },

    // 监听批次号列表变化，重新计算全选状态
    filteredBatchList: {
      handler() {
        if (this.filteredBatchList.length === 0) {
          this.batchSelectAll = false
          return
        }
        const currentPageBatchNos = this.filteredBatchList.map(item => item.batchNo)
        const selectedInCurrentPage = this.tempSelectedBatchNos.filter(batchNo =>
          currentPageBatchNos.includes(batchNo)
        )
        this.batchSelectAll = selectedInCurrentPage.length === this.filteredBatchList.length
      },
      deep: true
    }
  }
}
</script>

<style lang="less">
/* 抽屉主体修复 */
.el-drawer__body {
  padding: 0 !important;
  overflow: hidden !important;
}

/* 批次号选择容器 - flex垂直布局 */
.batch-select-container {
  padding: 10px 10px 0 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

/* 搜索栏 */
.batch-search-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.selected-count {
  margin-left: auto;
  color: #409eff;
  font-weight: bold;
}

/* 底部按钮 - 垂直水平居中 */
.batch-drawer-footer {
  margin-top: 18px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.batch-drawer-footer .el-button {
  min-width: 100px;
}

.batch-drawer-footer .el-button--default {
  background-color: #f5f7fa;
  border-color: #dcdfe6;
  color: #606266;
}

.batch-drawer-footer .el-button--default:hover {
  background-color: #ecf5ff;
  border-color: #c6e2ff;
  color: #409eff;
}

.batch-drawer-footer .el-button--primary {
  background-color: #409eff;
  border-color: #409eff;
}

.batch-drawer-footer .el-button--primary:hover {
  background-color: #66b1ff;
  border-color: #66b1ff;
}
</style>

<style lang="less" scoped>
/* 操作列 - 按钮紧凑 */
.button-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: 32px;
  width: 100%;
}

.button-item {
  flex: 1;
  height: 28px;
  min-width: 0;
  max-width: 50px;
  text-align: center;
}

.button-item .el-button {
  font-size: 12px;
  height: 100%;
  width: 100%;
  padding: 0 3px;
  white-space: nowrap;
}

// 覆盖 Element UI 默认图标间距
.button-item .el-button [class*="el-icon-"] {
  margin-right: 0 !important;
}

// 按钮文字不换行
.button-item .el-button span {
  display: inline;
  white-space: nowrap;
  font-size: 12px;
}

.batch-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.batch-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 6px;
}

.batch-popover-content {
  max-height: 400px;
  overflow-y: auto;
  padding: 12px;
}

.batch-popover-title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.batch-popover-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 350px;
  overflow-y: auto;
}

/* 备注样式 */
.remark-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  max-width: 100%;
}

.remark-text {
  display: inline-block;
  max-width: 280px;
  word-break: break-all;
}

.remark-ellipsis {
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 1; /* 允许文本收缩 */
  min-width: 0; /* 允许文本收缩到更小 */
}


// 新增用户对话框 - 左右布局
.add-user-layout {
  display: flex;
  gap: 0;
  height: 300px;
  padding: 10px 0;
}

.add-user-left {
  flex: 1;
  min-width: 0;
  padding-right: 20px;
  height: 100%;
  overflow: visible;

  .el-form-item {
    margin-bottom: 22px;

    &:last-child {
      margin-bottom: 0;
    }

    .el-form-item__label {
      width: 90px !important;
      white-space: nowrap;
      text-align: right;
      padding-right: 12px;
      line-height: 32px;
    }

    .el-form-item__content {
      margin-left: 90px !important;
      line-height: 32px;
      overflow: visible !important;
    }
  }

  .el-input,
  .el-select {
    .el-input__inner {
      height: 32px;
      line-height: 32px;
    }
  }

  .el-select {
    width: 100% !important;
  }
}

.add-user-divider {
  width: 1px;
  background: #dcdfe6;
  flex-shrink: 0;
  margin: 0 16px;
}

.add-user-right {
  flex: 0 0 220px;
  min-width: 0;
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  .batch-title {
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    margin-bottom: 6px;
    padding-bottom: 4px;
    border-bottom: 1px solid #ebeef5;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;

    .batch-count {
      font-size: 12px;
      color: #909399;
      font-weight: normal;
    }
  }

  .batch-select-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    overflow: hidden;

    .batch-empty {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #c0c4cc;
      font-size: 13px;
      gap: 8px;

      i {
        font-size: 32px;
      }
    }

    .batch-list-scroll {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 2px 0;
      min-height: 0;
      min-width: 0;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        background: #f5f7fa;
        border-radius: 2px;
      }

      &::-webkit-scrollbar-thumb {
        background: #dcdfe6;
        border-radius: 2px;

        &:hover {
          background: #c0c4cc;
        }
      }

      .batch-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 7px 10px;
        margin-bottom: 8px;
        background: #ecf5ff;
        border: 1px solid #d9ecff;
        border-radius: 4px;
        transition: background 0.2s, border-color 0.2s;
        min-width: 0;
        max-width: 100%;
        overflow: hidden;

        &:hover {
          background: #d9ecff;
          border-color: #b3d8ff;

          .batch-item-close {
            color: #f56c6c;
          }
        }

        .batch-item-text {
          color: #409eff;
          font-size: 12px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
          min-width: 0;
          max-width: 100%;
          font-weight: 500;
          cursor: pointer; // 添加鼠标指针，提示可悬浮查看
        }

        .batch-item-close {
          font-size: 14px;
          color: #a0cfff;
          cursor: pointer;
          padding: 0 4px;
          transition: color 0.2s;
          flex-shrink: 0;
          line-height: 1;

          &:hover {
            color: #f56c6c;
          }
        }
      }
    }

    .batch-select-btn {
      width: 100%;
      flex-shrink: 0;
      height: 28px;
      padding: 0 12px;
      font-size: 12px;
      margin-top: 4px;
    }
  }
}

// 底部按钮
.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 6px 0 0 0;

  .el-button {
    min-width: 80px;
    padding: 8px 24px;
    font-size: 13px;
  }
}
</style>
